import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Loader2, Sparkles } from 'lucide-react';
import FeatureBadge from '@/components/FeatureBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const AddMindModal = ({ isOpen, onClose, roomId, onSuccess }) => {
  const [name, setName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    if (!name.trim() || !roomId) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/minds/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), room_id: roomId }),
      });
      if (!res.ok) throw new Error('Generation failed');
      const data = await res.json();
      if (onSuccess) onSuccess(data);
      setName('');
      onClose();
    } catch (e) {
      setError('Failed to summon this mind. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(26,21,18,0.25)', backdropFilter: 'blur(16px)' }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="w-full max-w-md overflow-hidden"
          style={{ background: '#FDFCFB', border: '1px solid #E6E1DB', borderRadius: '16px', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.12)' }}
          initial={{ scale: 0.93, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 20 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #E6E1DB' }}>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" style={{ color: '#B8860B' }} />
              <h2 className="font-display text-lg tracking-wide" style={{ color: '#1A1512' }}>Summon a Mind</h2>
              <FeatureBadge feature="customMind" />
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all hover:bg-[#F2EFE9]" style={{ color: '#8C8680' }}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5">
            <p className="font-body text-sm mb-4" style={{ color: '#8C8680' }}>
              Enter a historical figure, thinker, or creator. This experimental flow creates a generated profile when the live engine is awake; portraits may use placeholders.
            </p>

            <div className="mb-4">
              <label className="font-ui text-[10px] tracking-wider uppercase mb-1.5 block" style={{ color: '#8C8680' }}>Name</label>
              <input
                data-testid="add-mind-name-input"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                placeholder="e.g. Nikola Tesla, Cleopatra, Virginia Woolf..."
                className="w-full px-4 py-3 rounded-lg text-sm font-body outline-none transition-all"
                style={{ background: '#F2EFE9', border: '1px solid #E6E1DB', color: '#1A1512' }}
                onFocus={e => e.target.style.borderColor = '#B8860B'}
                onBlur={e => e.target.style.borderColor = '#E6E1DB'}
              />
            </div>

            {error && <p className="text-xs mb-3" style={{ color: '#C0392B' }}>{error}</p>}

            <button
              data-testid="add-mind-submit-btn"
              onClick={handleGenerate}
              disabled={!name.trim() || generating}
              className="w-full py-3.5 font-display text-xs tracking-[0.2em] uppercase flex items-center justify-center gap-2 transition-all cursor-pointer"
              style={{ background: '#1A1512', color: '#FDFCFB', opacity: name.trim() && !generating ? 1 : 0.35, boxShadow: '0 4px 12px rgba(26,21,18,0.12)' }}
            >
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Summoning...</> : <><Plus className="w-4 h-4" /> Summon Mind</>}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddMindModal;
